import { Link } from "react-router-dom";
import { RefreshIcon } from "../components/Icons.jsx";
import MetricInfoTooltip from "../components/MetricInfoTooltip.jsx";
import { formatEpoch } from "../utils/formatters.js";
import {
  getCampaignProgress,
  getCampaignStatusLabel,
  getStatusClassName,
} from "../utils/history.js";
import { formatRate, getRedemptionMetrics } from "../utils/redemptionMetrics.js";
import { CAMPAIGN_PERFORMANCE_HELP } from "../utils/metricDefinitions.js";
import "../styles/campaign-detail.css";

function DetailItem({ label, children }) {
  return (
    <div className="cg-detail-item">
      <span>{label}</span>
      <strong>{children ?? "N/A"}</strong>
    </div>
  );
}

export default function CampaignDetailPage({
  campaign,
  loading,
  refreshing,
  error,
  isRetrying,
  canManageCampaigns,
  onRefresh,
  onRetry,
}) {
  if (loading && !campaign) {
    return <main className="cg-page-shell"><div className="cg-empty-state">Loading campaign...</div></main>;
  }

  if (error && !campaign) {
    return (
      <main className="cg-page-shell">
        <section className="cg-panel cg-page-card">
          <p className="cg-error-text">{error}</p>
          <Link className="cg-text-link" to="/cologuard">Return to campaigns</Link>
        </section>
      </main>
    );
  }

  const progress = getCampaignProgress(campaign);
  const status = String(campaign?.campaign_status || "");
  const canRetry = canManageCampaigns && status === "generation_error";
  const remainingCodes = Math.max(0, progress.requested - progress.stored);
  const performance = getRedemptionMetrics(campaign?.inventory);

  return (
    <main className="cg-page-shell">
      <div className="cg-page-frame">
        <section className="cg-panel cg-page-card cg-detail-page">
          <div className="cg-detail-heading">
            <div>
              <Link className="cg-text-link" to="/cologuard">← Back to campaigns</Link>
              <span className="cg-kicker">Campaign Detail</span>
              <h2>{campaign.campaign_name || campaign.campaign_designator}</h2>
              <p className="cg-muted-text">{campaign.campaign_designator} · Created by {campaign.created_by_email || campaign.created_by_name || "Unknown user"}</p>
            </div>
            <div className="cg-detail-actions">
              <span className={`cg-status-pill ${getStatusClassName(status)}`}>{getCampaignStatusLabel(status)}</span>
              <button type="button" className="cg-page-refresh-button" onClick={() => onRefresh({ silent: true })} disabled={refreshing || isRetrying}>
                <RefreshIcon />
                {refreshing ? "Refreshing..." : "Refresh data"}
              </button>
              {canRetry ? (
                <button type="button" onClick={onRetry} disabled={isRetrying}>
                  {isRetrying ? "Trying again..." : "Try again"}
                </button>
              ) : null}
            </div>
          </div>

          <section className="cg-detail-progress" aria-label={`${progress.percent}% ready`}>
            <div>
              <span>Code preparation</span>
              <strong>{progress.percent}%</strong>
            </div>
            <div className="cg-progress-track"><span style={{ width: `${progress.percent}%` }} /></div>
            <p>{progress.stored.toLocaleString()} of {progress.requested.toLocaleString()} codes are ready.</p>
          </section>

          {campaign.last_error ? (
            <section className="cg-detail-error" role="alert">
              <strong>Code preparation needs attention</strong>
              <p>Preparation stopped before all codes were ready. You can try again or contact support.</p>
            </section>
          ) : null}

          <div className="cg-detail-sections">
            <section>
              <h3>Campaign performance</h3>
              {performance.available ? (
                <>
                  <div className="cg-detail-grid cg-detail-performance-grid">
                    <DetailItem label={<MetricInfoTooltip label="Delivered" description={CAMPAIGN_PERFORMANCE_HELP.delivered} align="start" />}>
                      {performance.delivered.toLocaleString()}
                    </DetailItem>
                    <DetailItem label={<MetricInfoTooltip label="Order redemption" description={CAMPAIGN_PERFORMANCE_HELP.orderRedemption} />}>
                      {formatRate(performance.orderRedemptionRate)} ({performance.orderRedemptionCodes.toLocaleString()} of {performance.delivered.toLocaleString()})
                    </DetailItem>
                    <DetailItem label={<MetricInfoTooltip label="Code released" description={CAMPAIGN_PERFORMANCE_HELP.codeReleased} />}>
                      {formatRate(performance.codeReleasedRate)} ({performance.codeReleasedCodes.toLocaleString()} of {performance.delivered.toLocaleString()})
                    </DetailItem>
                    <DetailItem label={<MetricInfoTooltip label="Unrealized" description={CAMPAIGN_PERFORMANCE_HELP.unrealized} />}>
                      {formatRate(performance.unrealizedRate)} ({performance.unrealizedCodes.toLocaleString()} of {performance.delivered.toLocaleString()})
                    </DetailItem>
                    <DetailItem label={<MetricInfoTooltip label="True redemption" description={CAMPAIGN_PERFORMANCE_HELP.trueRedemption} align="end" />}>
                      {formatRate(performance.trueRedemptionRate)} ({performance.trueRedemptionCodes.toLocaleString()} of {performance.delivered.toLocaleString()})
                    </DetailItem>
                  </div>
                  <p className="cg-muted-text cg-performance-definition">
                    All percentages use delivered codes as the baseline. Unrealized represents released codes without an associated order; true redemption represents released codes with an associated order.
                  </p>
                </>
              ) : (
                <p className="cg-muted-text">
                  Performance totals are being prepared for this campaign.
                </p>
              )}
            </section>

            <section>
              <h3>Code status</h3>
              <div className="cg-detail-grid">
                <DetailItem label="Requested">{progress.requested.toLocaleString()}</DetailItem>
                <DetailItem label="Ready">{progress.stored.toLocaleString()}</DetailItem>
                <DetailItem label="Remaining">{remainingCodes.toLocaleString()}</DetailItem>
              </div>
            </section>

            <section>
              <h3>Campaign information</h3>
              <div className="cg-detail-grid">
                <DetailItem label="Designator">{campaign.campaign_designator}</DetailItem>
                <DetailItem label="Active dates">{campaign.from_date || "N/A"} — {campaign.to_date || "No end date"}</DetailItem>
                <DetailItem label="Created by">{campaign.created_by_email || campaign.created_by_name || "N/A"}</DetailItem>
              </div>
            </section>

            <section>
              <h3>Timeline</h3>
              <div className="cg-detail-grid">
                <DetailItem label="Created">{formatEpoch(campaign.created_at)}</DetailItem>
                <DetailItem label="Requested">{formatEpoch(campaign.generation_requested_at)}</DetailItem>
                <DetailItem label="Started">{formatEpoch(campaign.generation_started_at)}</DetailItem>
                <DetailItem label="Completed">{formatEpoch(campaign.generation_completed_at)}</DetailItem>
                <DetailItem label="Last updated">{formatEpoch(campaign.updated_at)}</DetailItem>
                <DetailItem label="Retry count">{Number(campaign.retry_count || 0)}</DetailItem>
                <DetailItem label="Last retry">{formatEpoch(campaign.last_retry_requested_at)}</DetailItem>
                <DetailItem label="Retried by">{campaign.last_retry_requested_by_email || campaign.last_retry_requested_by_user_id || "N/A"}</DetailItem>
              </div>
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}
